import { defineStore } from "pinia";
import {
  getRequest,
  putRequest,
  postRequest,
  deleteRequest,
  bulkDeleteRequest,
} from "~/apis/requests/useApiCall";
import { Template } from "~/types/replyTemplates";
import { Pagination } from "~/types/pagination";
import { TableDataWithPagination } from "~/types/generics/tableDataWithPagination";
import { PermissionNames } from "~/configs/permission_names";
import { isUserPermitted } from "~/utils/permissionHelper";
import { useAuthStore } from "~/stores/auth";

/**
 * The agent id every template request must be pinned to, or null when the user
 * may see everyone's templates.
 *
 * The gate lives here rather than in the list components because templates are
 * fetched from four places - the management page, the inbox picker, the picker's
 * pre-fetch in MessageBox, and the composer's own suggestion cache - and only
 * two of those show a filter the user could be locked out of.
 *
 * Falls back to -1, an id no user has, rather than to "no filter" if the auth
 * user is somehow not loaded yet. 0 would not do: the backend applies the filter
 * through `when($assignTo)`, which reads 0 as "unfiltered".
 */
const restrictedAssignee = (): number | null => {
  if (isUserPermitted(PermissionNames.REPLY_TEMPLATE_SHOW_OTHERS)) {
    return null;
  }

  return useAuthStore().user?.id ?? -1;
};

export const useReplyTemplatesStore = defineStore("replyTemplates", {
  state: () => ({
    _tableData: [] as Template[],
    _pagination: {} as Pagination,
    multipleSelection: [] as Array<Template>,
    _loading: true,
    _filterData: {},
    _selectedTemplateForEdit: {} as Template,
    // Why the last save was refused, shown inside the form the agent is in.
    _saveError: "",
    // Keyed by the API's field path ("templates.0"), so the form can put each
    // message under the input it belongs to.
    _saveFieldErrors: {} as Record<string, string>,
    selectedTemplates: [] as Array<Template>,
    showTemplateCreateModal: false,
    showTemplateEditModal: false,
    showTemplatesListDialog: false,
    // Full, un-paginated cache of every reply template, used by the inbox
    // composer to suggest saved replies entirely on the frontend.
    _allTemplates: [] as Template[],
    _allTemplatesLoaded: false,
    _allTemplatesLoading: false,
  }),

  actions: {
    async getTemplates(
      pageNo = 1,
      sortBy = "updated_at",
      ascending = false,
      append = false,
      perPage = 10,
      search = "",
      userId: number | null = null,
    ) {
      this._loading = true;
      const sortDir = ascending ? "asc" : "desc";

      let url = `desk/reply-templates?page=${pageNo}&per_page=${perPage}&pagination=true&sort_column=${sortBy}&dir=${sortDir}&search=${encodeURIComponent(
        search,
      )}&assign_to=${userId ?? ""}`;
      for (const [key, value] of Object.entries(this._filterData)) {
        if (!value) continue;
        url += `&${key}=${value}`;
      }

      // The last word on assign_to, appended after _filterData so neither the
      // caller nor a persisted filter can widen it - PHP keeps the last value of
      // a repeated query parameter.
      const restrictedTo = restrictedAssignee();
      if (restrictedTo !== null) {
        url += `&assign_to=${restrictedTo}`;
      }

      const { response, error } = await getRequest<
        TableDataWithPagination<Template>
      >(url);

      if (!error) {
        const { data, ...pagination } = response.data;

        if (append) {
          this.$state._tableData = [...this.$state._tableData, ...data];
        } else {
          this.$state._tableData = data;
        }

        this.$state._pagination = pagination;
      }

      this._loading = false;
    },

    // Fetch EVERY reply template once (no pagination) so the inbox composer can
    // suggest matches by title + body on the frontend without per-keystroke API
    // calls. Kept separate from the paginated _tableData the management table
    // uses. Idempotent: skips when already loaded or in flight (pass force to
    // refetch after a mutation).
    async getAllTemplates(force = false) {
      if (!force && (this._allTemplatesLoaded || this._allTemplatesLoading)) {
        return;
      }
      this._allTemplatesLoading = true;
      // pagination=false asks the API for the full set; per_page is a safety net
      // in case the backend still paginates (it then returns everything in one
      // large page). We accept either a bare array or a { data, ...pagination }
      // envelope.
      // The composer suggests straight out of this cache without passing through
      // either list UI, so it needs the same gate.
      const restrictedTo = restrictedAssignee();

      const { response, error } = await getRequest<any>(
        `desk/reply-templates?pagination=false&per_page=1000&sort_column=title&dir=asc${
          restrictedTo !== null ? `&assign_to=${restrictedTo}` : ""
        }`,
      );
      if (!error) {
        const data = response.data;
        this._allTemplates = Array.isArray(data) ? data : data?.data ?? [];
        this._allTemplatesLoaded = true;
      }
      this._allTemplatesLoading = false;
    },

    async createTemplate(template: any) {
      const { response, error } = await postRequest<Template>(
        "desk/reply-templates",
        template,
        true,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        },
      );

      if (!error) {
        this.clearSaveError();
        this._allTemplatesLoaded = false; // composer cache is now stale
        return response.data;
      }

      const data = error?.response?.data;

      this._saveFieldErrors = Object.fromEntries(
        Object.entries(data?.errors ?? {}).map(([field, messages]) => [
          field,
          (messages as string[])[0],
        ]),
      );
      this._saveError = data?.message || error?.message || "Request failed.";

      return null;
    },
    async getTemplate(id: number) {
      const { response, error } = await getRequest<Template>(
        `desk/reply-templates/${id}`,
      );
      if (!error) {
        this.$state._selectedTemplateForEdit = response.data;
        this._loading = false;
      }
    },
    async updateTemplate(template: any, id: number) {
      const { response, error } = await postRequest<Template>(
        `desk/reply-templates/${id}`,
        template,
        true,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            "X-HTTP-Method-Override": "PUT",
          },
        },
      );
      if (!error) {
        this.clearSaveError();
        this._allTemplatesLoaded = false; // composer cache is now stale
        return response.data;
      }

      const data = error?.response?.data;

      this._saveFieldErrors = Object.fromEntries(
        Object.entries(data?.errors ?? {}).map(([field, messages]) => [
          field,
          (messages as string[])[0],
        ]),
      );
      this._saveError = data?.message || error?.message || "Request failed.";

      return null;
    },
    async bulkDeleteTemplates(ids: number[]) {
      this._allTemplatesLoaded = false; // composer cache is now stale
      return await bulkDeleteRequest("desk/reply-templates/bulk", {
        ids,
      });
    },

    selectionChange(arr: Array<Template>) {
      this.multipleSelection = arr;
      this.selectedTemplates = arr;
    },
    async deleteTemplate(id: number) {
      this._allTemplatesLoaded = false; // composer cache is now stale
      return await deleteRequest(`desk/reply-templates/${id}`);
    },
    setSaveFieldErrors(errors: Record<string, string>) {
      this.$state._saveError = "";
      this.$state._saveFieldErrors = errors;
    },
    clearSaveError() {
      this.$state._saveError = "";
      this.$state._saveFieldErrors = {};
    },
    setTemplateCreateModalVisibility(val: boolean) {
      this.clearSaveError();
      this.$state.showTemplateCreateModal = val;
    },
    setTemplateEditModalVisibility(val: boolean) {
      this.clearSaveError();
      this.$state.showTemplateEditModal = val;
    },
    setTemplatesListDialogVisibility(val: boolean) {
      this.$state.showTemplatesListDialog = val;
    },
    setFilterData(filter: any) {
      this.$state._filterData = filter;
    },
  },
  getters: {
    tableData: (state) => state._tableData,
    pagination: (state) => state._pagination,
    selectedTemplateForEdit: (state) => state._selectedTemplateForEdit,
    allTemplates: (state) => state._allTemplates,
    saveError: (state) => state._saveError,
    saveFieldErrors: (state) => state._saveFieldErrors,
  },
});
